import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '../../hooks/useDebounce';
import { useSupplier, useSuppliersInfinite } from '../../hooks/useWmsSuppliers';
import type { Supplier, SupplierKind, SupplierStatus } from '../../types';

const ALL_VALUE = 'all';

interface SupplierInfiniteComboboxProps {
  value?: string;
  onChange: (supplierId: string) => void;
  onSupplierSelected?: (supplier: Supplier | null) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  fallbackLabel?: string;
  status?: SupplierStatus | 'all';
  /** Defaults to 'hardware' — Receiving + SKU pickers must only show hardware vendors.
   *  Pass `undefined` to allow all kinds (e.g. carrier picker). */
  kind?: SupplierKind;
  /** Optional tag filter (e.g. 'carrier'). Combined as AND with kind/status. */
  tag?: string | string[];
}

export function SupplierInfiniteCombobox({
  value,
  onChange,
  onSupplierSelected,
  disabled = false,
  className,
  placeholder = 'No supplier',
  fallbackLabel,
  status = 'active',
  kind = 'hardware',
  tag,
}: SupplierInfiniteComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useSuppliersInfinite(debouncedSearch, status, kind, tag);

  const loadedSuppliers = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.suppliers) ?? [],
    [infiniteQuery.data],
  );

  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  const isExplicitValue = !!value && value !== ALL_VALUE;
  const selectedFromLoaded = isExplicitValue
    ? loadedSuppliers.find((supplier) => supplier.id === value)
    : undefined;
  const fallbackLookupId = isExplicitValue && !selectedFromLoaded ? value : undefined;
  const fallbackQuery = useSupplier(fallbackLookupId);
  const selected = selectedFromLoaded ?? fallbackQuery.data ?? null;
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = infiniteQuery;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting
          && hasNextPage
          && !isFetchingNextPage
        ) {
          void fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  ]);

  const triggerLabel = selected?.name ?? fallbackLabel ?? placeholder;
  const showingLabel = total > 0 ? `Showing ${loadedSuppliers.length} of ${total}` : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-full text-xs justify-between font-normal',
            !selected && !fallbackLabel && 'text-muted-foreground',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[260px] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError
                ? 'Failed to load. Click to retry.'
                : infiniteQuery.isLoading
                  ? 'Loading...'
                  : 'No suppliers found.'}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(ALL_VALUE);
                  onSupplierSelected?.(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    !value || value === ALL_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {placeholder}
              </CommandItem>
              {loadedSuppliers.map((supplier) => (
                <CommandItem
                  key={supplier.id}
                  value={supplier.id}
                  onSelect={() => {
                    onChange(supplier.id);
                    onSupplierSelected?.(supplier);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === supplier.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <div className="min-w-0">
                    <div className="truncate">{supplier.name}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {supplier.account_number}
                    </div>
                  </div>
                </CommandItem>
              ))}
              {hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
